/**
 * Utility functions for mapping between document types, task IDs, and section IDs
 * This helps ensure consistent document categorization across the application
 */

/**
 * Map of task IDs to their corresponding section IDs
 * Each task is associated with a specific section for document categorization
 */
export const TASK_SECTION_MAP = {
  // Analysis section
  'project-info': 'analysis',
  'extract-prd': 'analysis',
  'features': 'analysis',
  'user-roles': 'analysis',
  'user-stories': 'analysis',
  'process-flows': 'analysis',
  'field-validations': 'analysis',
  'user-permissions': 'analysis',
  
  // UI/UX section
  'design-references': 'ui-ux',
  'wireframes': 'ui-ux',
  'design-guidelines': 'ui-ux', 
  'designs': 'ui-ux',
  'design-system': 'ui-ux',
  'knowledge-hub-design': 'ui-ux',
  
  // Coding & Development section
  'architecture': 'coding-development',
  'api-guidelines': 'coding-development',
  'tech-stack': 'coding-development',
  'dev-guidelines': 'coding-development',
  'security-guidelines': 'coding-development',
  'knowledge-hub-coding': 'coding-development',
  
  // Quality section (testing)
  'functional-testing': 'quality',
  'unit-testing': 'quality',
  'automated-testing': 'quality',
  'security-testing': 'quality',
  'performance-testing': 'quality',
  'knowledge-hub-testing': 'quality',
  
  // Infrastructure section
  'git-guidelines': 'infrastructure',
  'version-control': 'infrastructure',
  'cicd-pipeline': 'infrastructure',
  'deployment': 'infrastructure',
  'knowledge-hub-infrastructure': 'infrastructure'
};

/**
 * Map of task IDs to their corresponding document types
 * This ensures consistent document type assignment
 */
export const TASK_DOCTYPE_MAP = {
  // Analysis section
  'project-info': 'PROJECT_INFO',
  'extract-prd': 'PRD',
  'features': 'FEATURES',
  'user-roles': 'USER_ROLES',
  'user-stories': 'USER_STORIES',
  'process-flows': 'PROCESS_FLOWS',
  'field-validations': 'FIELD_VALIDATIONS',
  'user-permissions': 'USER_PERMISSIONS',
  
  // UI/UX section
  'design-references': 'DESIGN_REFERENCES',
  'wireframes': 'WIREFRAMES',
  'design-guidelines': 'DESIGN_GUIDELINES',
  'designs': 'DESIGNS',
  'design-system': 'DESIGN_SYSTEM',
  'knowledge-hub-design': 'KNOWLEDGE_HUB_DESIGN',
  
  // Coding & Development section
  'architecture': 'ARCHITECTURE',
  'api-guidelines': 'API_GUIDELINES',
  'tech-stack': 'TECH_STACK',
  'dev-guidelines': 'DEV_GUIDELINES',
  'security-guidelines': 'SECURITY_GUIDELINES',
  'knowledge-hub-coding': 'KNOWLEDGE_HUB_CODING',
  
  // Quality section
  'functional-testing': 'FUNCTIONAL_TESTING',
  'unit-testing': 'UNIT_TESTING',
  'automated-testing': 'AUTOMATED_TESTING',
  'security-testing': 'SECURITY_TESTING',
  'performance-testing': 'PERFORMANCE_TESTING',
  'knowledge-hub-testing': 'KNOWLEDGE_HUB_TESTING',
  
  // Infrastructure section
  'git-guidelines': 'GIT_GUIDELINES',
  'version-control': 'VERSION_CONTROL',
  'cicd-pipeline': 'CICD_PIPELINE',
  'deployment': 'DEPLOYMENT',
  'knowledge-hub-infrastructure': 'KNOWLEDGE_HUB_INFRASTRUCTURE'
};

/**
 * Reverse mapping from document types to task IDs
 * Used when we have a document type but need to find its associated task
 */
export const DOCTYPE_TASK_MAP = Object.entries(TASK_DOCTYPE_MAP).reduce((acc, [key, value]) => {
  acc[value] = key;
  return acc;
}, {});

/**
 * Get the section ID for a given task ID
 * @param {string} taskId - The task ID
 * @returns {string|undefined} - The section ID or undefined if not found
 */
export const getSectionIdForTask = (taskId) => {
  return TASK_SECTION_MAP[taskId] || undefined;
};

/**
 * Get the document type for a given task ID
 * @param {string} taskId - The task ID
 * @returns {string|undefined} - The document type or undefined if not found
 */
export const getDocumentTypeForTask = (taskId) => {
  return TASK_DOCTYPE_MAP[taskId] || undefined;
};

/**
 * Get the task ID for a given document type
 * @param {string} documentType - The document type
 * @returns {string|undefined} - The task ID or undefined if not found
 */
export const getTaskIdForDocumentType = (documentType) => {
  return DOCTYPE_TASK_MAP[documentType] || undefined;
};

/**
 * Get a standardized document data object with proper task and section IDs
 * @param {Object} documentData - The document data object
 * @returns {Object} - Document data with proper taskId and sectionId
 */
export const getStandardizedDocumentData = (documentData) => {
  const { taskId, sectionId, type } = documentData;
  
  let standardTaskId = taskId;
  let standardSectionId = sectionId;
  
  // If taskId is missing but type is provided, derive taskId from type
  if (!standardTaskId && type) {
    standardTaskId = getTaskIdForDocumentType(type);
  }
  
  // If sectionId is missing but taskId is now available, derive sectionId from taskId
  if (!standardSectionId && standardTaskId) {
    standardSectionId = getSectionIdForTask(standardTaskId);
  }
  
  return {
    ...documentData,
    taskId: standardTaskId,
    sectionId: standardSectionId
  };
};

/**
 * Validates document data to ensure it has proper taskId and sectionId
 * @param {Object} documentData - The document data to validate
 * @returns {Object} - Result with isValid flag and any error messages
 */
export const validateDocumentData = (documentData) => {
  if (!documentData) {
    return {
      isValid: false,
      errors: ['Document data is missing']
    };
  }
  
  const errors = [];
  
  // Ensure required fields are present
  if (!documentData.title) {
    errors.push('Document title is required');
  }
  
  if (!documentData.content) {
    errors.push('Document content is required');
  }
  
  if (!documentData.type) {
    errors.push('Document type is required');
  }
  
  // Check for taskId and sectionId
  const { taskId, sectionId, type } = documentData;
  
  // Try to determine taskId from document type if missing
  let validTaskId = taskId;
  if (!validTaskId && type) {
    validTaskId = getTaskIdForDocumentType(type);
    if (!validTaskId) {
      errors.push(`Could not determine taskId for document type: ${type}`);
    }
  } else if (!validTaskId) {
    errors.push('Document taskId is missing and could not be determined');
  }
  
  // Try to determine sectionId from taskId if missing
  let validSectionId = sectionId;
  if (!validSectionId && validTaskId) {
    validSectionId = getSectionIdForTask(validTaskId);
    if (!validSectionId) {
      errors.push(`Could not determine sectionId for taskId: ${validTaskId}`);
    }
  } else if (!validSectionId) {
    errors.push('Document sectionId is missing and could not be determined');
  }
  
  return {
    isValid: errors.length === 0,
    errors,
    enhancedData: errors.length === 0 ? {
      ...documentData,
      taskId: validTaskId,
      sectionId: validSectionId
    } : null
  };
};